<script>
export default {
  name: "BanobagiTransactionList",
  props: {
    transactions: {
      type: Array,
      default: () => []
    }
  },
  emits: ["toggle"],
  methods: {
    formatRights(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return "";
      const sign = number > 0 ? "+" : "";
      return sign + this.numeralFormat(number) + " " + this.$t("right_unit");
    }
  }
};
</script>

<template>
  <div class="banobagi-transaction-list" data-test="transaction-list">
    <article
      v-for="item in transactions"
      :key="item._key"
      class="banobagi-transaction-list__item"
      :data-test="'transaction-list-item-' + item._key"
    >
      <button
        type="button"
        class="banobagi-transaction-list__toggle"
        :data-test="'transaction-list-item-' + item._key + '-btn'"
        :aria-expanded="item._isExpanded"
        :aria-controls="'transaction-detail-' + item._key"
        :aria-label="
          item._categoryName +
          ', ' +
          (item._isExpanded ? $t('close') : $t('see_detail'))
        "
        @click="$emit('toggle', item)"
      >
        <span class="banobagi-transaction-list__heading">
          <strong>{{ item._categoryName }}</strong>
          <span
            v-if="item._rightsDelta !== null"
            :class="[
              'banobagi-transaction-list__rights',
              { 'is-negative': item._rightsDelta < 0 }
            ]"
          >
            {{ formatRights(item._rightsDelta) }}
          </span>
        </span>

        <span class="banobagi-transaction-list__meta-row">
          <span>{{ $t("date") }} {{ item._formattedDate }}</span>
          <span class="banobagi-transaction-list__detail-label">
            {{ item._isExpanded ? $t("close") : $t("see_detail") }}
            <font-awesome-icon
              :icon="['fas', item._isExpanded ? 'chevron-up' : 'chevron-down']"
              aria-hidden="true"
            />
          </span>
        </span>

        <span class="banobagi-transaction-list__branch">
          {{ item._branchName }}
        </span>
      </button>

      <div
        v-if="item._isExpanded"
        :id="'transaction-detail-' + item._key"
        class="banobagi-transaction-list__expanded"
        :data-test="'transaction-detail-' + item._key"
      >
        <div v-if="item._invoiceNo" class="banobagi-transaction-list__invoice">
          <span>{{ $t("invoice_no") }}</span>
          <strong>{{ item._invoiceNo }}</strong>
        </div>

        <template v-if="item._detail">
          <div class="banobagi-transaction-list__products">
            <div class="banobagi-transaction-list__product-heading">
              <span>{{ $t("description") }}</span>
              <span> {{ $t("amount") }}/{{ $t("grand_total") }} </span>
            </div>

            <div
              v-for="product in item._detail.products"
              :key="product._key"
              class="banobagi-transaction-list__product"
            >
              <span>{{ product.name }}</span>
              <span class="banobagi-transaction-list__product-amount">
                <span>{{ numeralFormat(product.quantity) }}</span>
                <span>
                  {{ $t("currency_sign") }}
                  {{ numeralFormat(product.total, "0,0[.]00") }}
                </span>
              </span>
            </div>

            <div
              v-if="item._detail.products.length === 0"
              class="banobagi-transaction-list__no-products"
            >
              {{ $t("no_data") }}
            </div>
          </div>

          <div class="banobagi-transaction-list__total">
            <span>
              {{ numeralFormat(item._detail.products.length) }}
              {{ $t("list") }}
            </span>
            <strong>
              {{ $t("currency_sign") }}
              {{ numeralFormat(item._detail.grandTotal, "0,0[.]00") }}
            </strong>
          </div>
        </template>
      </div>
    </article>

    <div
      v-if="transactions.length === 0"
      class="banobagi-transaction-list__empty"
      data-test="transaction-list-empty"
    >
      {{ $t("transaction_empty") }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.banobagi-transaction-list {
  padding: 0 24px;
}

.banobagi-transaction-list__item {
  padding: 16px 0;
  border-bottom: 1px solid var(--disable-color);
}

.banobagi-transaction-list__toggle {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-color);
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.banobagi-transaction-list__toggle:focus-visible {
  border-radius: 8px;
  outline: 2px solid var(--primary-color);
  outline-offset: 3px;
}

.banobagi-transaction-list__heading,
.banobagi-transaction-list__meta-row,
.banobagi-transaction-list__invoice,
.banobagi-transaction-list__product-heading,
.banobagi-transaction-list__product,
.banobagi-transaction-list__total {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.banobagi-transaction-list__heading {
  width: 100%;
  font-size: var(--font-title-sm);
  line-height: 1.35;
}

.banobagi-transaction-list__heading strong,
.banobagi-transaction-list__rights,
.banobagi-transaction-list__invoice strong,
.banobagi-transaction-list__total strong {
  font-weight: 600;
}

.banobagi-transaction-list__rights {
  flex: 0 0 auto;
  color: #22aa62;
  font-variant-numeric: tabular-nums;
}

.banobagi-transaction-list__rights.is-negative {
  color: #d82525;
}

.banobagi-transaction-list__meta-row {
  width: 100%;
  color: var(--crm-link-color, #8d8d8d);
  font-size: var(--font-body-sm);
  line-height: 1.4;
}

.banobagi-transaction-list__detail-label {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
}

.banobagi-transaction-list__branch {
  color: var(--crm-link-color, #8d8d8d);
  font-size: var(--font-body-sm);
  line-height: 1.4;
}

.banobagi-transaction-list__branch::before {
  content: "• ";
}

.banobagi-transaction-list__expanded {
  padding-top: 12px;
}

.banobagi-transaction-list__invoice {
  margin-bottom: 12px;
  color: var(--crm-link-color, #8d8d8d);
  font-size: var(--font-body-sm);
  line-height: 1.4;
}

.banobagi-transaction-list__invoice strong {
  color: var(--text-color);
  overflow-wrap: anywhere;
  text-align: right;
}

.banobagi-transaction-list__empty,
.banobagi-transaction-list__no-products {
  color: var(--crm-link-color, #8d8d8d);
  font-size: var(--font-body-sm);
  line-height: 1.5;
  text-align: center;
}

.banobagi-transaction-list__products {
  padding: 12px;
  border-radius: 12px;
  background: color-mix(
    in srgb,
    var(--disable-color) 16%,
    var(--secondary-color)
  );
}

.banobagi-transaction-list__product-heading {
  color: var(--crm-link-color, #8d8d8d);
  font-size: var(--font-body-sm);
  line-height: 1.4;
}

.banobagi-transaction-list__product {
  margin-top: 8px;
  font-size: var(--font-body-sm);
  line-height: 1.45;
}

.banobagi-transaction-list__product > span:first-child {
  min-width: 0;
  overflow-wrap: anywhere;
}

.banobagi-transaction-list__product-amount {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  font-variant-numeric: tabular-nums;
}

.banobagi-transaction-list__no-products {
  padding-top: 12px;
}

.banobagi-transaction-list__total {
  margin-top: 8px;
  font-size: var(--font-body-sm);
  font-variant-numeric: tabular-nums;
  line-height: 1.45;
}

.banobagi-transaction-list__empty {
  padding: 32px 16px;
}
</style>
